import { useEffect, useState } from 'react';
import { Form, ResourceContextProvider, useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { FieldValues } from 'react-hook-form';
import { Box, InputAdornment, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import _ from 'lodash';
import { useTranslate } from 'ra-core';
import authProvider from '../../../utils/authProvider';
import { typedHttpClient } from '../../../utils/httpClient';
import {
  AppConfigConfigType,
  AppConfigDefinitionType,
} from '../../../lib/constants/typedApiReturnTypes';
import { ClientConfigFormGenerator } from '../../../lib/clientConfigFormGenerator';
import {
  hasClientConfigDefaultValueChanged,
  mergeConfigWithPartialFormSubmission,
  prepareTopLevelClientConfigDefaultForUpdate,
} from '../../../lib/clientConfigHelpers';

export const AppConfigDefaults = () => {

  const [definition, setDefinition] = useState<AppConfigDefinitionType | null>(null);
  const [config, setConfig] = useState<AppConfigConfigType | undefined>();
  const [updateTimestamp, setUpdateTimestamp] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState('');
  const notify = useNotify();
  const translate = useTranslate();
  const navigate = useNavigate();
  const isDevAdmin = authProvider.isDeveloperAdmin();

  useEffect(() => {

    if (!isDevAdmin) {

      notify('resources.config.text.no_access');
      navigate(-1);

    }

  }, [isDevAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {

    typedHttpClient().clientConfig.clientConfigControllerResolveAppDefaultConfigV0().then((response) => {

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      setDefinition(response.data.definition);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      setConfig(response.data.config);

    });

  }, [updateTimestamp]);

  const updateConfig = async (values: FieldValues) => {

    if (!definition) {

      return;

    }
    const mergedValues = mergeConfigWithPartialFormSubmission(
      config as Record<string, unknown> | undefined,
      values as Record<string, unknown>,
    );
    const updatePromises = [];

    for (const def of definition) {

      const originalValue = _.get(config, def.key);
      const mergedValue = _.get(mergedValues, def.key);
      const newValue = prepareTopLevelClientConfigDefaultForUpdate(mergedValue, originalValue, def.schema);

      if (hasClientConfigDefaultValueChanged(originalValue, newValue)) {

        const id = def.mergeHistory[def.mergeHistory.length - 1].sources[0]._id;
        const updatePromise = typedHttpClient().clientConfig.clientConfigControllerUpdateKey(id, { defaultValue: newValue });
        updatePromises.push(updatePromise);

      }

    }

    const results = await Promise.all(updatePromises);
    const hasError = results.some((result) => result.status >= 400);
    if (hasError) {

      notify('resources.config.text.defaults_update_error', { type: 'error' });

    } else {

      notify('resources.config.text.defaults_updated');

    }
    setUpdateTimestamp(Date.now());

  };

  return (
    <div>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 2,
      }}>
        <h1 style={{ margin: 0 }}>{translate('menu.items.app-defaults')}</h1>
        <TextField
          size="small"
          placeholder={translate('ra.action.search')}
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          sx={{ width: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      <ResourceContextProvider value={'ClientConfig'}>
        {/* noValidate: collapsed groups use display:none; browser HTML5 validation cannot focus required hidden inputs */}
        <Form noValidate reValidateMode={'onChange'} record={config} mode="onBlur" onSubmit={updateConfig} sanitizeEmptyValues>
          {definition && <ClientConfigFormGenerator definitions={definition} searchQuery={searchTerm} />}
        </Form>
      </ResourceContextProvider>
    </div>
  );

};
